import { AnalyticsOutlined, ArrowForwardOutlined, BusinessOutlined as BusinessIcon, ShoppingCartOutlined as OrdersIcon, PersonOutline as PatientIcon, PeopleOutline as PeopleIcon, AssignmentTurnedInOutlined as RecordTxIcon, AttachMoneyOutlined as SalesIcon } from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import React from "react";
import Chart from "react-apexcharts";
import { tokens } from "../../theme";
import { priceFormatter } from "../../utils/Utility";
import CustomHelpMessage from "../common/CustomHelpMessage";
import CustomTooltip from "../common/CustomTooltip";

// KPI Type for icon mapping
export type KPIType =
  | "activeUsers"
  | "activePractices"
  | "totalPatients"
  | "weeklySales"
  | "weeklyOrders"
  | "recordTx";

interface Props {
  title: string;
  total: number;
  percentage: number;
  duration: string;
  chartData: number[];
  labels?: string[];
  color?: "success" | "error" | "warning" | "info" | "cyan" | "purple";
  tooltipMessage: string;
  loading?: boolean;
  kpiType?: KPIType;
  onOpenModal?: (kpiType: KPIType) => void;
}

const colorMap: Record<string, string> = {
  success: "#10b981",
  error: "#ef4444",
  warning: "#f59e0b",
  info: "#3b82f6",
  cyan: "#06b6d4",
  purple: "#8b5cf6",
};

// KPI Icon mapping
const kpiIconMap: Record<KPIType, React.ReactNode> = {
  activeUsers: <PeopleIcon />,
  activePractices: <BusinessIcon />,
  totalPatients: <PatientIcon />,
  weeklySales: <SalesIcon />,
  weeklyOrders: <OrdersIcon />,
  recordTx: <RecordTxIcon />,
};

/* ===================== */
/* Loading Animations    */
/* ===================== */

const ShimmerPill: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box
      role="status"
      aria-label="loading value"
      sx={{
        position: "relative",
        width: 90,
        height: 42,
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${isDark ? "#2f3544" : "#cfd6e3"}`,
        background: isDark
          ? "linear-gradient(135deg, #131826, #182234)"
          : "linear-gradient(135deg, #e8edf7, #f3f6fb)",
        "::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, transparent 0%, rgba(255,255,255,.08) 20%, rgba(255,255,255,.22) 35%, transparent 60%)",
          transform: "translateX(-100%)",
          animation: "pillShine 1.6s ease-in-out infinite",
        },
        "@keyframes pillShine": {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(100%)" },
        },
      }}
    />
  );
};

const LoadingEllipsis: React.FC = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Typography sx={{ fontWeight: 700, opacity: 0.85 }}>Loading</Typography>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: "#3b82f6",
          boxShadow: "0 0 10px rgba(59,130,246,.45)",
          animation: `dotWave 1.2s ${i * 0.12}s infinite`,
          "@keyframes dotWave": {
            "0%, 60%, 100%": { transform: "translateY(0) scale(.8)", opacity: 0.5 },
            "30%": { transform: "translateY(-4px) scale(1)", opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

const LoadingChartPlaceholder: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const base = isDark ? "#222839" : "#e6eaf3";
  const heights = [26, 36, 52, 44];

  return (
    <Box
      sx={{
        position: "relative",
        height: 60,
        width: 100,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 1,
        "::before": {
          content: '""',
          position: "absolute",
          inset: 0,
        },
        "@keyframes sweep": {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(100%)" },
        },
      }}
    >
      {heights.map((h, i) => (
        <Box
          key={i}
          sx={{
            position: "relative",
            width: 12,
            height: h,
            borderRadius: 1,
            overflow: "hidden",
            backgroundColor: base,
            boxShadow: isDark
              ? "0 4px 14px rgba(0,0,0,.35), inset 0 0 0 1px #35405a"
              : "0 4px 10px rgba(0,0,0,.08), inset 0 0 0 1px #d1d7e4",
            animation: `barFloat 1.4s ease-in-out ${i * 0.12}s infinite`,
            "@keyframes barFloat": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-8%)" },
            },
            "::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(255,255,255,.10), rgba(255,255,255,0))",
              mixBlendMode: "screen",
            },
          }}
        />
      ))}
    </Box>
  );
};

const TotalActiveUsersCard: React.FC<Props> = ({
  title,
  total,
  percentage,
  duration,
  chartData,
  labels,
  color = "success",
  tooltipMessage,
  loading = false,
  kpiType,
  onOpenModal,
}) => {
  const theme = useTheme();
  const mainColor = colorMap[color] || "#10b981";
  const ArrowIcon =
    percentage > 0
      ? ArrowUpwardIcon
      : percentage < 0
      ? ArrowDownwardIcon
      : ArrowForwardOutlined;
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const desiredCount =
    Array.isArray(labels) && labels.length > 0
      ? labels.length
      : chartData?.length || 4;

  const normalizedData = Array.from(
    { length: desiredCount },
    (_, i) => chartData?.[i] ?? 0
  );
  const barTooltipName =
    Array.isArray(labels) && labels.length === desiredCount
      ? labels
      : Array.from({ length: desiredCount }, (_, i) => `W${i + 1}`);

  const zeroBarColor = isDark ? "#2B2F3A" : "#E5E7EB";
  const perBarColors = normalizedData.map((v) =>
    v === 0 ? zeroBarColor : mainColor
  );

  const chartOptions: ApexOptions = {
    chart: { type: "bar", sparkline: { enabled: true }, toolbar: { show: false } },
    colors: perBarColors,
    plotOptions: { bar: { columnWidth: "60%", borderRadius: 4, distributed: true } },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 1,
      colors: [isDark ? "#374151" : "#D1D5DB"],
    },
    tooltip: {
      enabled: true,
      theme: "dark",
      x: {
        formatter: (_val, { dataPointIndex }) =>
          barTooltipName[dataPointIndex] || "",
      },
      y: {
        formatter: (val: number) => (val === 0 ? "No data" : val.toLocaleString()),
        title: { formatter: () => "" },
      },
    },
  };

  const totalText =
    title === "Weekly Sales"
      ? priceFormatter(total)
      : title === "Record Tx"
      // ? `${total.toLocaleString()} %`   // CS-1984: guarded below
      ? `${(total ?? 0).toLocaleString()} %`   // CS-1984
      // : total.toLocaleString();   // CS-1984: guarded below
      : (total ?? 0).toLocaleString();   // CS-1984

  const dir = percentage > 0 ? "↑ " : percentage < 0 ? "↓ " : "→ ";
  const pctTooltipLine =
    percentage === 0
      // ? `No change in ${title} (${labels && labels[0]}) compared to the previous ${duration == "4 weeks" ? duration : "week"} (${labels && labels[1]})`   // CS-1984: guarded below
      ? `No change in ${title}${labels?.[0] ? ` (${labels[0]})` : ""} compared to the previous ${duration == "4 weeks" ? duration : "week"}${labels?.[1] ? ` (${labels[1]})` : ""}`   // CS-1984
      : `${dir}${Math.abs(percentage)}% ${
          percentage > 0 ? "increase" : "decrease"
        } in ${title}  (${labels && labels[0]}) compared to the previous ${duration == "4 weeks" ? duration : "week"} (${labels && labels[1]})`;

  const handleOpenModal = () => {
    if (kpiType && onOpenModal) {
      onOpenModal(kpiType);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "#10131a" : "#ffffff",
        borderRadius: 3,
        p: 1.5,
        mb: 2,
        border: `1px solid ${isDark ? "#222733" : "#e5e9ed"}`,
        boxShadow: isDark ? "0 1px 0 rgba(255,255,255,0.04) inset" : "0 1px 3px rgba(15,23,42,0.04)",
        transition: "box-shadow .3s ease",
        position: "relative",
        ...(loading && {
          boxShadow:
            "0 0 0 1px rgba(59,130,246,.2), 0 10px 30px rgba(59,130,246,.12)",
        }),
      }}
    >
      {kpiType && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {onOpenModal && !loading && (
            <CustomTooltip title="View Details" arrow>
              <IconButton
                onClick={handleOpenModal}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: isDark ? "#1e2430" : "#e8eaed",
                  color: isDark ? "#8b949e" : "#57606a",
                  border: `1px solid ${isDark ? "#30363d" : "#d0d7de"}`,
                  transition: "all 0.2s ease",
                }}
              >
                <AnalyticsOutlined />
              </IconButton>
            </CustomTooltip>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <Typography 
        variant="h4"
          fontWeight={700} 
          sx={{ 
            letterSpacing: 0.2,
          }}
        >
          {title} <CustomHelpMessage message={tooltipMessage} />
        </Typography>
      </Box>

      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          gap: 0.75,
        }}
      >
        <Box 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            flexWrap: "wrap",
            gap: 0.75,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* AMOUNT */}
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
              <ShimmerPill />
              <LoadingEllipsis />
            </Box>
          ) : (
            <Box
              sx={{
                border: `1px solid ${isDark ? "#464954ff" : "#e2e8f0"}`,
                borderRadius: 1.5,
                py: 0.5,
                px: 0.75,
                background: isDark
                  ? "linear-gradient(270deg, #202024ff, #1d2931)"
                  : "linear-gradient(270deg, #e7e6e6ff, #edededff)",
              }}
            >
              <Typography 
                fontWeight={700} 
                variant="h4"
                sx={{ 
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                {totalText}
              </Typography>
            </Box>
          )}

          {/* PERCENTAGE */}
          {!loading && (
            <CustomTooltip title={pctTooltipLine || ""} arrow>
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  whiteSpace: "nowrap",
                }}
              >
                {React.createElement(
                  percentage > 0
                    ? ArrowUpwardIcon
                    : percentage < 0
                      ? ArrowDownwardIcon
                      : ArrowForwardOutlined,
                  {
                    sx: {
                      mr: 0.25,
                      fontSize: 14,
                      color:
                        percentage > 0
                          ? tokens(isDark ? "dark" : "light").success[300]
                          : percentage < 0
                            ? tokens(isDark ? "dark" : "light").error[300]
                            : tokens(isDark ? "dark" : "light").warning[300],
                    },
                  }
                )}
                <Typography
                  fontWeight={700}
                  sx={{
                    fontSize: "0.75rem",
                    color:
                      percentage > 0
                        ? tokens(isDark ? "dark" : "light").success[300]
                        : percentage < 0
                          ? tokens(isDark ? "dark" : "light").error[300]
                          : tokens(isDark ? "dark" : "light").warning[300],
                  }}
                >
                  {`${percentage}%`}
                </Typography>
              </Box>
            </CustomTooltip>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {loading ? (
            <LoadingChartPlaceholder />
          ) : (
            <Chart
              options={chartOptions}
              series={[{ data: normalizedData }]}
              type="bar"
              height={45}
              width={70}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TotalActiveUsersCard;
